/**
 * Authentication state for the whole app.
 *
 * `useAuth` wraps a single TanStack Query against `GET /api/auth/me`.
 * The query cache IS the state — there's no separate React Context.
 * Any component that calls `useAuth` subscribes to the cached
 * response and re-renders when it changes (sign-in, sign-out,
 * status change by admin, etc.).
 *
 * A 401 response from `/me` is mapped to `null`, not an error. That's
 * the cleanest way to distinguish "not signed in" (valid state) from
 * "network failure" (real error that should bubble up).
 */

import { useQuery, type UseQueryResult } from "@tanstack/react-query";

import { ApiError, apiFetch } from "@/lib/api";

export type UserStatus = "pending_approval" | "active" | "disabled";

export type FamilyMemberRole = "owner" | "member";

export interface User {
  id: string;
  email: string;
  display_name: string;
  preferred_name: string | null;
  avatar_url: string | null;
  status: UserStatus;
  is_super_admin: boolean;
}

export interface FamilyMember {
  user_id: string;
  email: string;
  display_name: string;
  avatar_url: string | null;
  role: FamilyMemberRole;
  joined_at: string; // ISO datetime
}

export interface Family {
  id: string;
  name: string;
  owner_user_id: string;
  created_at: string; // ISO datetime
  location: string | null;
  budget_level: string | null;
  interests: string | null;
  members: FamilyMember[];
}

export interface AuthMeResponse {
  user: User;
  family: Family | null;
}

const AUTH_QUERY_KEY = ["auth", "me"] as const;

async function fetchMe(): Promise<AuthMeResponse | null> {
  try {
    return await apiFetch<AuthMeResponse>("/api/auth/me");
  } catch (error) {
    if (error instanceof ApiError && error.status === 401) {
      // Unauthenticated is a valid state, not an error.
      return null;
    }
    throw error;
  }
}

export interface AuthState {
  /** True while the initial /me request is in flight. */
  isLoading: boolean;
  /** True if /me surfaced a non-401 error (network / 5xx). */
  isError: boolean;
  /** The current user, or null if not signed in. */
  user: User | null;
  /** The current user's family, or null if they aren't in one. */
  family: Family | null;
  /** True when `user` is non-null, regardless of status. */
  isAuthenticated: boolean;
  /** True when the user is authenticated AND status === "active". */
  isActive: boolean;
  /** True when the user is active AND is_super_admin. */
  isSuperAdmin: boolean;
  /** Raw status, or null when unauthenticated. */
  status: UserStatus | null;
  /** True when the user is active AND has a family. */
  hasFamily: boolean;
}

export function useAuth(): AuthState {
  const query: UseQueryResult<AuthMeResponse | null, Error> = useQuery({
    queryKey: AUTH_QUERY_KEY,
    queryFn: fetchMe,
    retry: false,
    staleTime: 60_000,
  });

  const user = query.data?.user ?? null;
  const family = query.data?.family ?? null;
  const status = user?.status ?? null;

  return {
    isLoading: query.isLoading,
    isError: query.isError,
    user,
    family,
    isAuthenticated: user !== null,
    isActive: status === "active",
    isSuperAdmin: user?.is_super_admin === true && status === "active",
    status,
    hasFamily: status === "active" && family !== null,
  };
}

export { AUTH_QUERY_KEY };
